import { useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import LoadingSkeleton from "@shared/LoadingSkeleton";
import { getTitleCase } from "@utils/utils";
import { deleteSavedSearchById } from "./services";
import ViewSavedSearchModal from "./ViewSavedSearchModal";
import { datasetLabel } from "./utils";
import type { SavedSearchResponse, SavedSearchRow } from "./types";

type ProductSavedSearchesProps = {
  savedSearchData?: SavedSearchResponse;
  isLoading: boolean;
  onReload: (row: SavedSearchRow) => void;
  onDeleted: () => void;
};

const actionBtnCls = "tw-rounded-lg tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-text-primary-purple-600 hover:tw-bg-gray-200 tw-flex tw-items-center tw-text-center tw-cursor-pointer tw-gap-1";
const directionLabel = (tradeDirection?: string) => (tradeDirection === "export" ? "Suppliers" : "Buyers");

const groupByDate = (rows: SavedSearchRow[]): Record<string, SavedSearchRow[]> => {
  return rows.reduce((groups: Record<string, SavedSearchRow[]>, row) => {
    const parsed = row.created_at ? moment(row.created_at, "DD MMM, YYYY") : null;
    const label = parsed?.isValid() ? parsed.format("MMM DD, YYYY") : row.created_at || "Unknown";
    if (!groups[label]) groups[label] = [];
    groups[label].push(row);
    return groups;
  }, {});
};

const ProductSavedSearches = ({ savedSearchData, isLoading, onReload, onDeleted }: ProductSavedSearchesProps) => {
  const [deletingId, setDeletingId] = useState<number | string | null>(null);
  const [viewingRow, setViewingRow] = useState<SavedSearchRow | null>(null);

  const handleDelete = async (row: SavedSearchRow) => {
    setDeletingId(row.id);
    try {
      const res = await deleteSavedSearchById(row.id);
      if (res?.status === "success") {
        toast.success(res.message || "Saved search deleted");
        onDeleted();
      } else {
        toast.error(res?.message || "Something went wrong. Please try again later!");
      }
    } catch {
      toast.error("Something went wrong. Please try again later!");
    } finally {
      setDeletingId(null);
    }
  };

  const items = Array.isArray(savedSearchData?.data) ? savedSearchData.data : [];

  return (
    <div className="tw-flex-1 tw-min-h-0 tw-overflow-auto tw-p-3 tw-pt-[19px] tw-rounded-lg tw-shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] tw-border tw-border-gray-200 tw-bg-white">
      <div className="tw-space-y-4 tw-h-full">
        {items.length ? (
          Object.entries(groupByDate(items)).map(([date, dateItems]) => (
            <div key={date} className="tw-flex tw-flex-col tw-gap-2">
              <div className="tw-font-medium tw-text-xs tw-text-gray-500 tw-border-b tw-border-gray-200 tw-pb-1">{date}</div>
              {dateItems.map((row) => (
                <div key={row.id} className="tw-border tw-border-gray-200 tw-px-2 tw-py-3 tw-rounded-lg tw-flex tw-justify-between tw-items-center">
                  <div className="tw-flex tw-flex-col tw-gap-1">
                    <span className="tw-text-sm tw-font-medium tw-text-gray-max">
                      {getTitleCase((row.search_name || "").length > 25 ? `${row.search_name.slice(0, 25)}...` : row.search_name || "")}
                      <span className="material-symbols-outlined tw-cursor-pointer tw-text-gray-500 tw-ml-2 tw-align-middle" style={{ fontSize: "16px" }} onClick={() => setViewingRow(row)}>
                        zoom_out_map
                      </span>
                    </span>
                    <span className="tw-text-sm tw-font-normal tw-text-gray-900">
                      {directionLabel(row.trade_direction)}
                      {datasetLabel(row.search_data?.datasetType) ? (
                        <>
                          {" "}
                          <span className="tw-text-gray-500">&middot;</span> {datasetLabel(row.search_data?.datasetType)}
                        </>
                      ) : null}
                    </span>
                  </div>
                  <div className="tw-text-sm tw-font-medium tw-text-primary-purple-500 tw-gap-2 tw-flex tw-items-center">
                    <div className={actionBtnCls} onClick={() => onReload(row)}>
                      Reload
                      <svg xmlns="http://www.w3.org/2000/svg" height="14px" viewBox="0 -960 960 960" width="14px" fill="#7143f1">
                        <path d="M348.87-116.6q-61.14-26.6-106.4-71.87-45.27-45.26-71.87-106.4Q144-356 144-426h72q0 109 77.5 186.5T480-162q109 0 186.5-77.5T744-426q0-109-77.5-186.5T479.61-690H474l51 51-51 51-138-138 138-138 51 51-52 51h7q70 0 131.13 26.6 61.14 26.6 106.4 71.87 45.27 45.26 71.87 106.4Q816-496 816-426t-26.6 131.13q-26.6 61.14-71.87 106.4-45.26 45.27-106.4 71.87Q550-90 480-90t-131.13-26.6Z" />
                      </svg>
                    </div>
                    <div className={actionBtnCls} onClick={() => (deletingId === row.id ? null : handleDelete(row))}>
                      <svg fill="#7143f1" xmlns="http://www.w3.org/2000/svg" height="16" viewBox="0 -960 960 960" width="16">
                        <path d="M292.309-140.001q-29.923 0-51.115-21.193-21.193-21.192-21.193-51.115V-720h-40v-59.999H360v-35.384h240v35.384h179.999V-720h-40v507.691q0 30.308-21 51.308t-51.308 21H292.309ZM680-720H280v507.691q0 5.385 3.462 8.847 3.462 3.462 8.847 3.462h375.382q4.616 0 8.463-3.846 3.846-3.847 3.846-8.463V-720ZM376.155-280h59.999v-360h-59.999v360Zm147.691 0h59.999v-360h-59.999v360ZM280-720v520-520Z" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))
        ) : isLoading ? (
          <LoadingSkeleton lines={5} lineHeight="70px" width="100%" shape="rectangle" />
        ) : (
          <div className="tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-text-center tw-px-6">
            <div className="tw-w-14 tw-h-14 tw-rounded-2xl tw-bg-saved-icon-bg tw-flex tw-items-center tw-justify-center tw-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" height="28px" viewBox="0 -960 960 960" width="28px" fill="#617cff">
                <path d="M220-150v-597.69Q220-778 241-799q21-21 51.31-21h375.38Q698-820 719-799q21 21 21 51.31V-150L480-261.54 220-150Z" />
              </svg>
            </div>
            <div>
              <p className="tw-font-semibold tw-text-sm tw-text-gray-900 tw-m-0">Nothing saved yet</p>
              <p className="tw-text-sm tw-text-gray-500 tw-mt-1 tw-mb-0">Save a search to reuse its terms and filters in one click.</p>
            </div>
          </div>
        )}
      </div>
      <ViewSavedSearchModal row={viewingRow} onClose={() => setViewingRow(null)} />
    </div>
  );
};

export default ProductSavedSearches;
